// SunControls.ts
import * as THREE from "three";
import { spa_calculate } from "./NREL";
import { GetDateVal, GetRadVal, GetRoundNum } from "../Controllers/Common";
import { useViewerStore } from "../../../../../store/store";

// ---- constants ----
export const radiusOut = 120;
export const radiusSun = 5;

export interface SunInfo {
    date: Date;
    lat: number;
    lon: number;
    zone: number;
    time: number;
    elevation: number;
    utcOffset ?: number;
}

export const labelMonthArr = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
] as const;

export interface BuildingAlt {
    low: number;
    high: number;
}

export interface ControllerSelf {
    sunInfo: SunInfo;
    buildingAlt?: BuildingAlt | null;
    sunGroup: THREE.Group;
    sunVecArr?: SunVecArr;
}

export interface TimeInfo {
    year: number;
    month: number; // 1-12
    day: number;   // 1-31
    hour: number;  // 0-23
    min: number;   // 0-59
}

export interface SunVec {
    x: number;
    y: number;
    z: number;
}

// sunVecArr[month][day][hour] -> SunVec
export type SunVecArr = SunVec[][][];

// ---- helpers ----
function dayOfYearToDate(dayOfYear: number): string {
    const currentYear = new Date().getFullYear();
    const date = new Date(currentYear, 0, dayOfYear);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${month}-${day}-${year}`;
}

// ---- API ----
export function LoadSunGroup(sunGroup: THREE.Group): void {
    const sunGeo = new THREE.SphereGeometry(radiusSun, 32, 32);
    const sunMat = new THREE.MeshStandardMaterial({
        color: 0xfdb813,
        emissive: 0xfdb813,
    });
    const sunMesh = new THREE.Mesh(sunGeo, sunMat);
    const sunLight = new THREE.PointLight(0xffffff, 1);

    sunLight.name = "sunLight";
    sunLight.add(sunMesh);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.width = 1024;
    sunLight.shadow.mapSize.height = 1024;

    sunGroup.add(sunLight);
    // mirror Z for your coordinate convention
    sunGroup.scale.z = -1;
}

export function SetSunVecArr(self: ControllerSelf): void {
    const { sunInfo, buildingAlt } = self;
    if (!sunInfo?.utcOffset || !buildingAlt || buildingAlt.low === 0.1) return;

    const { low, high } = buildingAlt;
    const elevation = (low + high) / 2;
    sunInfo.elevation = elevation;

    self.sunVecArr = CreateSunVecArr(sunInfo);
    SetSunPathGeo(self.sunGroup, self.sunVecArr, sunInfo);
}

export function CreateSunVecArr(sunInfo: SunInfo): SunVecArr {
    const year = new Date().getFullYear();
    const sunVecArr: SunVecArr = [];
    const dateFirst = new Date(year, 0, 1);
    const countDecimal = 5;

    for (let i = 1; i < 366; i++) {
        const dateNew = new Date(dateFirst);
        dateNew.setDate(i);
        sunInfo.date = GetDateVal(dateNew) as Date;

        const day = dateNew.getDate() - 1;
        const month = dateNew.getMonth();
        if (!sunVecArr[month]) sunVecArr[month] = [];

        sunVecArr[month][day] = [];
        for (let j = 0; j < 24; j++) {
            sunInfo.time = j;
            const timeInfo: TimeInfo = {
                year,
                month: month + 1,
                day: day + 1,
                hour: j === 23 ? 0 : j + 1,
                min: 55,
            };
            const { x, y, z } = SetSunPos(null, sunInfo, timeInfo, "init");
            sunVecArr[month][day].push({
                x: GetRoundNum(x, countDecimal),
                y: GetRoundNum(y, countDecimal),
                z: GetRoundNum(z, countDecimal),
            });
        }
    }

    return sunVecArr;
}

export function SetSunPathGeo(
    sunGroup: THREE.Group,
    sunVecArr: SunVecArr,
    info: SunInfo
): void {
    const oldSunPathMesh = sunGroup.children.find(
        (child) => (child as THREE.Object3D).name === "sunPath"
);
    if (oldSunPathMesh) {
        sunGroup.remove(oldSunPathMesh);
    }

    const sunPathGeo = new THREE.BufferGeometry();
    const sunPathMat = new THREE.LineBasicMaterial({ color: 0x3250b4 });

    const mainVerArr: number[] = [];
    const analemmaMat = new THREE.LineDashedMaterial({
        color: 0xff9b75,
        linewidth: 0.5, // note: linewidth is mostly ignored by WebGL implementations
    });

    const analemmaLineArr: THREE.LineLoop[] = [];

    const { date } = info;
    const year = new Date().getFullYear();

    for (let t = 0; t < 24; t++) {
        const timeVal = new Date(date);
        timeVal.setHours(t, 0, 0, 0);

        const month = timeVal.getMonth();
        const day = timeVal.getDate() - 1;

        const vector = sunVecArr?.[month]?.[day]?.[t];
        if (!vector) continue;

        mainVerArr.push(vector.x, vector.y, vector.z);

        // Build analemma line (all days for fixed hour)
        const analemmaGeo = new THREE.BufferGeometry();
        const vertices: number[] = [];

        for (let i = 1; i < 366; i++) {
            const subTimeVal = new Date(year, 0, i);
            const subMonth = subTimeVal.getMonth();
            const subDay = subTimeVal.getDate() - 1;

            const v = sunVecArr?.[subMonth]?.[subDay]?.[t];
            if (!v) continue;
            vertices.push(v.x, v.y, v.z);
        }

        analemmaGeo.setAttribute(
            "position",
            new THREE.BufferAttribute(new Float32Array(vertices), 3)
        );
        const analemmaLine = new THREE.LineLoop(analemmaGeo, analemmaMat);
        analemmaLine.computeLineDistances();
        analemmaLineArr.push(analemmaLine);
    }

    sunPathGeo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(mainVerArr), 3)
    );

    const sunPathMesh = new THREE.LineLoop(sunPathGeo, sunPathMat);
    sunPathMesh.visible = false;
    sunPathMesh.name = "sunPath";

    analemmaLineArr.forEach((line) => {
        sunPathMesh.add(line);
    });

    sunGroup.add(sunPathMesh);

    const labelSunDate = document.getElementById("sunDate");
    if (labelSunDate) {
        labelSunDate.textContent =
            "Select Date:" +
            date.getDate() +
            " / " +
            labelMonthArr[date.getMonth()] +
            " / " +
            date.getFullYear();
    }
}

// ---- SetSunPos overloads ----
export function SetSunPos(
    sunGroup: null,
    info: SunInfo,
    timeInfo: TimeInfo,
    type: "init"
): SunVec;
export function SetSunPos(
    sunGroup: THREE.Group,
    info: SunInfo,
    timeInfo: TimeInfo,
    type?: undefined
): void;
export function SetSunPos(
    sunGroup: THREE.Group | null,
    info: SunInfo,
    timeInfo: TimeInfo,
    type?: "init"
): SunVec | void {
    // SPA-based computation
    const axisDir = 1;
    const { utcOffset, lat, lon, elevation = 0 } = info;
    const { year, month, day, hour, min } = timeInfo;

    const spa = {
        year,
        month,
        day,
        hour,
        min,
        timezone: utcOffset ?? 0,
        longitude: lon,
        latitude: lat,
        elevation,
        pressure: 1013,
        temperature: 11,
        slope: 0,
        azm_rotation: 0,
        atmos_refract: 0.5667,
        delta_ut1: 0,
        delta_t: 64.797,
    };

    const { d_azimuth, d_zenith }: { d_azimuth: number; d_zenith: number } =
        spa_calculate(spa);

    const azimuth = GetRadVal(d_azimuth) as number;
    const altitude = GetRadVal(90 - d_zenith) as number;

    const x = radiusOut * Math.cos(altitude) * Math.sin(azimuth) * axisDir;
    const z = radiusOut * Math.cos(altitude) * Math.cos(azimuth) * axisDir * -1;
    const y = radiusOut * Math.sin(altitude);

    if (type === "init") {
        return { x, y, z };
    }

    if (sunGroup) {
        const sunLight = sunGroup.children.find(
            (child) => (child as THREE.Object3D).name === "sunLight"
    ) as THREE.PointLight | undefined;

        if (sunLight) {
            sunLight.position.set(x, y, -z);
        }

        const labelSunTime = document.getElementById("labelSunTime");
        // format to 12h with leading zeros
        let hh = hour;
        let mm = min;
        const ampm = hh >= 12 ? "pm" : "am";
        hh = hh % 12;
        hh = hh ? hh : 12;
        const hhStr = (hh < 10 ? "0" : "") + hh.toString();
        const mmStr = (mm < 10 ? "0" : "") + mm.toString();

        if (labelSunTime) {
            labelSunTime.textContent = `Select Time : ${hhStr} : ${mmStr} ${ampm}`;
        }

        const sunDateFromStore = (useViewerStore.getState() as any).sunDate as number; // day-of-year?
        const dateToDisplay = dayOfYearToDate(sunDateFromStore);

        const labelSunDate = document.getElementById("sunDate");
        if (labelSunDate) {
            labelSunDate.textContent = `Select Date : ${dateToDisplay}`;
        }
    }
}

export function SetSunGroupPos(
    sunGroup: THREE.Group,
    sunInfo: SunInfo
): void {
    const { date, time } = sunInfo;
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = Math.floor(time);
    const min = Math.floor((time - hour) * 60);

    const timeInfo: TimeInfo = { year, month, day, hour, min };
    SetSunPos(sunGroup, sunInfo, timeInfo);
}

export function CheckSunTimePlay(self: ControllerSelf): void {
    const btnSunTimePlay = document.getElementsByClassName("btn-sun-time-play")[0];
    const rangeSunTimeEl = document.getElementById("rangeSunTime") as
    | HTMLInputElement
    | null;

    if (!btnSunTimePlay || !rangeSunTimeEl) return;
    const active = btnSunTimePlay.classList.contains("active");
    if (!active) return;

    let newTime = self.sunInfo.time + 1 / 60;
    if (newTime >= 24) newTime = 0;

    self.sunInfo.time = newTime;
    rangeSunTimeEl.value = String(newTime);

    SetSunGroupPos(self.sunGroup, self.sunInfo);
}
